<template>
  <div class="verifikasi-page">
    <div class="page-header">
      <h1 class="page-title">Permintaan Verifikasi</h1>
    </div>
    <!-- Search & Filters -->
    <div class="actions-bar">
      <a-row :gutter="[16, 16]">
        <a-col :xs="24" :sm="24" :md="24" :lg="24">
          <a-input
            v-model:value="searchText"
            placeholder="Cari nomor surat atau jenis surat"
            @input="handleSearch"
          >
            <template #prefix>
              <SearchOutlined style="color: rgba(0, 0, 0, 0.25); margin-right: 4px" />
            </template>
          </a-input>
        </a-col>
      </a-row>
    </div>

    <!-- Table -->
    <a-table
      :data-source="filteredData"
      :columns="columns"
      :row-key="record => record.surat.id"
      :loading="loading"
      :scroll="{ x: 1200 }"
      :pagination="false"
      class="verifikasi-table"
      :custom-row="customRow"
      @change="handleTableChange"
    >
      <template #nomorSurat="{ text }">
        {{ text || '-' }}
      </template>

      <template #tanggal="{ text }">
        {{ text ? formatDate(text) : '-' }}
      </template>

      <template #jenisSurat="{ text }">
        {{ text || '-' }}
      </template>

      <template #waktuDibuat="{ text }">
        {{ text ? formatDateTime(text) : '-' }}
      </template>

      <template #status="{ record }">
        <a-tag :color="getStatusColor(record)">
          <strong>{{ getStatusText(record) }}</strong>
        </a-tag>
      </template>
    </a-table>

    <!-- Pagination -->
    <div v-if="!loading && pagination.total > 0" class="pagination-container">
      <a-pagination
        v-model:current="pagination.current"
        v-model:page-size="pagination.pageSize"
        :total="pagination.total"
        :show-total="total => `Total ${total} baris`"
        :show-size-changer="true"
        :page-size-options="pagination.pageSizeOptions"
        @change="handlePageChange"
      />
    </div>
  </div>
</template>

<script>
import { onMounted, onBeforeUnmount, ref, computed, reactive, h } from 'vue'
import { useRouter } from 'vue-router'
import { SearchOutlined } from '@ant-design/icons-vue'
import { notification, DatePicker } from 'ant-design-vue'
import moment from 'moment'
import 'moment/locale/id'
import { verifikasiAPI } from '@/services/axios/perki'

moment.locale('id')

export default {
  name: 'VerifikasiSurat',
  components: {
    SearchOutlined,
  },
  setup() {
    const router = useRouter()
    const loading = ref(false)
    const dataTable = ref([])
    const searchText = ref('')
    const searchTimer = ref(null)

    const state = reactive({
      filteredInfo: null,
      // Default sort: createdAt desc (waktuDibuat column)
      sortedInfo: { field: 'waktuDibuat', order: 'descend' },
    })

    const pagination = reactive({
      current: 1,
      pageSize: 10,
      showSizeChanger: true,
      pageSizeOptions: ['10', '20', '50', '100'],
      total: 0,
    })

    const columns = [
      {
        title: 'No. Surat',
        dataIndex: ['surat', 'nomorSurat'],
        key: 'nomorSurat',
        width: 200,
        slots: { customRender: 'nomorSurat' },
      },
      {
        title: 'Tanggal Surat',
        dataIndex: ['surat', 'tanggalSurat'],
        key: 'tanggalSurat',
        width: 150,
        slots: { customRender: 'tanggal' },
        sorter: (a, b) => new Date(a.surat?.tanggalSurat) - new Date(b.surat?.tanggalSurat),
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) =>
          h('div', { style: { padding: '8px' } }, [
            h(DatePicker.RangePicker, {
              style: { marginBottom: '8px', display: 'block' },
              value: selectedKeys[0],
              onChange: e => setSelectedKeys(e ? [e] : []),
            }),
            h('div', { style: { display: 'flex', justifyContent: 'space-between' } }, [
              h(
                'a',
                {
                  style: { fontSize: '12px' },
                  onClick: () => {
                    clearFilters()
                  },
                },
                'Reset',
              ),
              h(
                'a',
                {
                  style: { fontSize: '12px' },
                  onClick: () => {
                    confirm()
                  },
                },
                'OK',
              ),
            ]),
          ]),
        onFilter: (value, record) => {
          if (!value || !Array.isArray(value) || value.length < 2 || !record.surat?.tanggalSurat) {
            return true
          }
          const recordDate = moment(record.surat.tanggalSurat)
          const startDate = moment(value[0])
          const endDate = moment(value[1])
          return recordDate.isBetween(startDate, endDate, 'day', '[]')
        },
      },
      {
        title: 'Jenis Surat',
        dataIndex: ['surat', 'jenisSurat'],
        key: 'jenisSurat',
        width: 200,
        slots: { customRender: 'jenisSurat' },
        filters: [
          { text: 'Surat Tugas', value: 'Surat Tugas' },
          { text: 'Cabang', value: 'Cabang' },
          { text: 'Farmasi', value: 'Farmasi' },
          { text: 'Instansi Pemerintah', value: 'Instansi Pemerintah' },
          { text: 'Internal - Surat', value: 'Internal - Surat' },
          { text: 'Internal - Undangan', value: 'Internal - Undangan' },
          { text: 'Kepegawaian PP', value: 'Kepegawaian PP' },
          { text: 'MoU', value: 'MoU' },
          { text: 'PKS', value: 'PKS' },
          { text: 'OPKeseminatan Lain', value: 'OPKeseminatan Lain' },
          { text: 'PB IDI', value: 'PB IDI' },
          { text: 'Perusahaan Swasta', value: 'Perusahaan Swasta' },
          { text: 'Podcast', value: 'Podcast' },
          { text: 'Pokja', value: 'Pokja' },
          { text: 'Rumah Sakit', value: 'Rumah Sakit' },
          { text: 'SK', value: 'SK' },
          { text: 'YJI', value: 'YJI' },
          { text: 'Lainnya', value: 'Lainnya' },
        ],
        onFilter: (value, record) => record.surat?.jenisSurat === value,
      },
      {
        title: 'Waktu Dibuat/Unggah',
        dataIndex: ['surat', 'createdAt'],
        key: 'waktuDibuat',
        width: 200,
        slots: { customRender: 'waktuDibuat' },
        sorter: (a, b) => new Date(a.surat?.createdAt) - new Date(b.surat?.createdAt),
        defaultSortOrder: 'descend',
      },
      {
        title: 'Status Surat',
        key: 'statusVerifikasi',
        width: 180,
        slots: { customRender: 'status' },
        filters: [
          { text: 'Butuh Verifikasi', value: 'pending' },
          { text: 'Dalam Antrian', value: 'dalam_antrian' },
          { text: 'Selesai', value: 'approved' },
          { text: 'Revisi Diajukan', value: 'revision_requested' },
          { text: 'Dibatalkan', value: 'rejected' },
        ],
      },
    ]

    // Data is provided from server; client-side filtering is minimal.
    const filteredData = computed(() => dataTable.value || [])

    const formatDate = dateString => {
      if (!dateString) return '-'
      return moment(dateString).format('D MMMM YYYY')
    }

    const formatDateTime = dateString => {
      if (!dateString) return '-'
      return moment(dateString).format('dddd YYYY-MM-DD HH:mm')
    }

    const getStatusColor = record => {
      const verifStatus = record.verifikasi?.status
      const suratStatus = record.surat?.status
      const isCurrentTurn = record.isCurrentTurn

      // Surat status takes priority for certain statuses
      if (suratStatus === 'Dibatalkan') {
        return 'default' // Gray for Dibatalkan
      }

      // Dalam Antrian: pending but not current turn
      if (verifStatus === 'pending' && !isCurrentTurn) {
        return 'blue'
      }

      const colors = {
        pending: 'red', // Butuh Verifikasi (current turn)
        approved: 'green',
        rejected: 'default',
        revised: 'orange',
        revision_requested: 'orange',
      }
      return colors[verifStatus] || 'default'
    }

    const getStatusText = record => {
      const verifStatus = record.verifikasi?.status
      const suratStatus = record.surat?.status
      const isCurrentTurn = record.isCurrentTurn

      // Surat status takes priority for certain statuses
      if (suratStatus === 'Dibatalkan') {
        return 'Dibatalkan'
      }

      // Dalam Antrian: pending but not current turn
      if (verifStatus === 'pending' && !isCurrentTurn) {
        return 'Dalam Antrian'
      }

      const texts = {
        pending: 'Butuh Verifikasi', // Only for current turn
        approved: 'Selesai',
        rejected: 'Dibatalkan',
        revised: 'Revisi',
        revision_requested: 'Revisi Diajukan',
      }
      return texts[verifStatus] || verifStatus || 'Pending'
    }

    const handleSearch = () => {
      pagination.current = 1
      if (searchTimer.value) clearTimeout(searchTimer.value)
      searchTimer.value = setTimeout(() => {
        fetchData()
      }, 300)
    }

    onBeforeUnmount(() => {
      if (searchTimer.value) clearTimeout(searchTimer.value)
    })

    // Handle table change (filters, sorter, pagination)
    const handleTableChange = (pag, filters, sorter) => {
      pagination.current = pag.current
      pagination.pageSize = pag.pageSize
      state.filteredInfo = filters
      state.sortedInfo = sorter
      fetchData()
    }

    const handlePageChange = (page, pageSize) => {
      pagination.current = page
      pagination.pageSize = pageSize
      fetchData()
    }

    const customRow = record => {
      return {
        onClick: () => {
          if (record.surat?.id) {
            router.push(`/verifikasi/${record.surat.id}`)
          }
        },
      }
    }

    const fetchData = async () => {
      loading.value = true
      try {
        // Build params
        const params = {
          page: pagination.current,
          pageSize: pagination.pageSize,
        }

        if (searchText.value && searchText.value.trim()) params.q = searchText.value.trim()

        // jenisSurat filter - backend supports single value, so we only take first
        // Note: Backend only supports single jenisSurat filter, so we take first
        if (state.filteredInfo?.jenisSurat && state.filteredInfo.jenisSurat.length > 0) {
          params.jenisSurat = state.filteredInfo.jenisSurat[0]
        }

        // statusVerifikasi filter - map to backend parameters
        // Backend accepts: verifikatorStatus (pending, approved, revision_requested), status (Dibatalkan), isCurrentTurn (true/false)
        // All support comma-separated values for multiple selection
        if (
          state.filteredInfo?.statusVerifikasi &&
          state.filteredInfo.statusVerifikasi.length > 0
        ) {
          const selectedStatuses = state.filteredInfo.statusVerifikasi
          const verifikatorStatuses = []
          let hasDibatalkan = false
          let hasButuhVerifikasi = false
          let hasDalamAntrian = false

          selectedStatuses.forEach(status => {
            if (status === 'pending') {
              hasButuhVerifikasi = true
              if (!verifikatorStatuses.includes('pending')) {
                verifikatorStatuses.push('pending')
              }
            } else if (status === 'dalam_antrian') {
              hasDalamAntrian = true
              if (!verifikatorStatuses.includes('pending')) {
                verifikatorStatuses.push('pending')
              }
            } else if (status === 'approved') {
              verifikatorStatuses.push('approved')
            } else if (status === 'revision_requested') {
              verifikatorStatuses.push('revision_requested')
            } else if (status === 'rejected') {
              // Dibatalkan is a surat status, not verifikator status
              hasDibatalkan = true
            }
          })

          // If only one of Butuh Verifikasi or Dalam Antrian is selected, send isCurrentTurn filter
          if (hasButuhVerifikasi && !hasDalamAntrian) {
            params.isCurrentTurn = 'true'
          } else if (hasDalamAntrian && !hasButuhVerifikasi) {
            params.isCurrentTurn = 'false'
          }
          // If both are selected, don't send isCurrentTurn (show all pending)

          if (verifikatorStatuses.length > 0) {
            params.verifikatorStatus = verifikatorStatuses.join(',')
          }
          if (hasDibatalkan) {
            params.status = 'Dibatalkan'
          }
        }

        // tanggal filter: expect filters.tanggalSurat = [ [startMoment, endMoment] ]
        if (state.filteredInfo?.tanggalSurat && state.filteredInfo.tanggalSurat.length > 0) {
          const range = state.filteredInfo.tanggalSurat[0]
          if (Array.isArray(range) && range.length === 2) {
            params.dateFrom = range[0].format('DD-MM-YYYY')
            params.dateTo = range[1].format('DD-MM-YYYY')
          }
        }

        // Sorting
        let sortBy = 'createdAt'
        let sortOrder = 'DESC'
        if (state.sortedInfo && state.sortedInfo.field) {
          if (state.sortedInfo.field === 'tanggalSurat') sortBy = 'tanggalSurat'
          else sortBy = 'createdAt'
        }
        if (state.sortedInfo && state.sortedInfo.order) {
          if (state.sortedInfo.order === 'ascend') sortOrder = 'ASC'
          else if (state.sortedInfo.order === 'descend') sortOrder = 'DESC'
          else {
            loading.value = false
            notification.error({ message: 'Error', description: 'Invalid sortOrder' })
            return
          }
        }
        params.sortBy = sortBy
        params.sortOrder = sortOrder

        console.debug('verifikasi.fetchData params:', params)

        const response = await verifikasiAPI.getSuratForVerifikasi(params)

        if (response.data?.status === 'success') {
          const d = response.data.data || {}
          dataTable.value = d.suratForVerifikasi || []
          const p = d.pagination || {}
          pagination.current = p.page || pagination.current
          pagination.pageSize = p.pageSize || pagination.pageSize
          pagination.total = p.totalRecords || 0
        } else {
          dataTable.value = []
          pagination.total = 0
        }
      } catch (error) {
        console.error('Error fetching verifikasi:', error)
        notification.error({
          message: 'Error',
          description: error.response?.data?.message || 'Gagal memuat data verifikasi',
        })
        dataTable.value = []
        pagination.total = 0
      } finally {
        loading.value = false
      }
    }

    onMounted(() => {
      fetchData()
    })

    return {
      loading,
      dataTable,
      searchText,
      columns,
      filteredData,
      pagination,
      formatDate,
      formatDateTime,
      getStatusColor,
      getStatusText,
      handleSearch,
      handleTableChange,
      handlePageChange,
      customRow,
    }
  },
}
</script>

<style scoped lang="scss">
.verifikasi-page {
  min-height: calc(100vh - 64px);
  background: #fcfcfc;
  padding: 24px;

  @media (max-width: 768px) {
    padding: 16px;
  }

  @media (max-width: 576px) {
    padding: 12px;
  }

  .page-header {
    margin-bottom: 24px;

    @media (max-width: 768px) {
      margin-bottom: 16px;
    }

    .page-title {
      font-size: 24px;
      font-weight: 600;
      color: #282828;
      margin: 0;

      @media (max-width: 576px) {
        font-size: 20px;
      }
    }
  }

  .actions-bar {
    margin-bottom: 24px;

    @media (max-width: 768px) {
      margin-bottom: 16px;
    }

    :deep(.ant-input::placeholder) {
      color: #999999 !important;
      opacity: 1 !important;
      font-size: 14px;
    }
  }

  .verifikasi-table {
    margin-bottom: 16px;

    :deep(.ant-table) {
      font-size: 14px;
    }

    :deep(.ant-table-thead > tr > th) {
      background: #f2f4f8 !important;
      font-weight: 600;
      color: #262626;
    }

    :deep(.ant-table-tbody > tr) {
      cursor: pointer;
      transition: background-color 0.2s;

      &:hover > td {
        background-color: #e4e9f0 !important;
      }

      > td {
        background: white !important;
      }
    }

    :deep(.ant-table-tbody > tr > td) {
      border-right: none !important;
    }

    :deep(.ant-table-thead > tr > th) {
      border-right: none !important;
    }
  }

  .pagination-container {
    display: flex;
    justify-content: flex-end;
    margin-top: 16px;

    @media (max-width: 576px) {
      justify-content: center;
    }
  }
}

::v-deep(.ant-tag) {
  font-weight: 700;
  border-radius: 50px;
}
</style>
